import React, { type FC, memo, use, useDeferredValue, useEffect, useState } from "react";
import { AppstoreOutlined, BarsOutlined, SearchOutlined } from "@ant-design/icons";
import { Flex, Space, Tooltip } from "antd";
import { FormattedMessage } from "react-intl";

import { SelectedFilterSwitch } from "@dms/components/Toolbar/components/SelectedFilterSwitch";
import { DocumentsApi } from "@dms/scripts/DocumentsApi/DocumentsApi";
import { DmsAppContext, DmsAppControlContext } from "@dms/types/ContextTypes";

import { DraftSelectBtn } from "./components/DraftSelectBtn";
import { MonthSelectBtn } from "./components/MonthSelectBtn";
import { YearSelectBtn } from "./components/YearSelectBtn";
import { Notification } from "./Notifications/Notification";
import { Tab, Tabs, Input } from "@heroui/react";

const Toolbar: FC = () => {
    const { view, searchValue } = use(DmsAppContext);
    const { setViewMode, setSearchValue } = use(DmsAppControlContext);

    const [inputValue, setInputValue] = useState(searchValue ?? "");
    const { selectedRowKeys } = use(DmsAppContext);
    const inputDeferred = useDeferredValue(inputValue);

    useEffect(() => {
        const handleSearch = (value: string) => {
            if (value.startsWith("https://")) {
                const middleUrl = DocumentsApi.urlFormatter(value);
                const index = middleUrl.lastIndexOf("%2F");
                value = middleUrl.slice(index + 3, value.length - 1);
            }

            setSearchValue(value);
        };
        handleSearch(inputDeferred);
    }, [inputDeferred]);

    return (
        <Flex justify={"space-between"}>
            <Notification />
            <Space>
                <YearSelectBtn />
                <MonthSelectBtn />
                <DraftSelectBtn />
                <SelectedFilterSwitch />
            </Space>

            <Space>
                <Input
                    isClearable
                    style={{ width: 230 }}
                    onValueChange={setInputValue}
                    value={inputValue}
                    isDisabled={selectedRowKeys.length > 0}
                    variant={"bordered"}
                    startContent={<SearchOutlined />}
                />
                <Tabs aria-label="DMS Mode" selectedKey={view} onSelectionChange={setViewMode}>
                    <Tab
                        key="list"
                        title={
                            <Tooltip title={<FormattedMessage id="app.dms.list" />} destroyOnHidden>
                                <span data-testid={"dms-toolbar-view-option-table"}>
                                    <BarsOutlined />
                                </span>
                            </Tooltip>
                        }
                    />
                    <Tab
                        key="grid"
                        title={
                            <Tooltip title={<FormattedMessage id="app.dms.grid" />} destroyOnHidden>
                                <span data-testid={"dms-toolbar-view-option-grid"}>
                                    <AppstoreOutlined />
                                </span>
                            </Tooltip>
                        }
                    />
                </Tabs>
            </Space>
        </Flex>
    );
};

export default memo(Toolbar);
